import React from 'react';
import { cn } from '../../utils';
import { getAvatarText, getAvatarColorIndex } from '../../utils/avatar';

const avatarColors = [
  'bg-red-100 text-red-700',
  'bg-orange-100 text-orange-700',
  'bg-amber-100 text-amber-700',
  'bg-yellow-100 text-yellow-700',
  'bg-lime-100 text-lime-700',
  'bg-green-100 text-green-700',
  'bg-emerald-100 text-emerald-700',
  'bg-teal-100 text-teal-700',
  'bg-cyan-100 text-cyan-700',
  'bg-blue-100 text-blue-700',
  'bg-indigo-100 text-indigo-700',
  'bg-violet-100 text-violet-700',
];

const sizeClasses = {
  xs: 'h-6 w-6 text-xs',
  sm: 'h-8 w-8 text-sm',
  md: 'h-10 w-10 text-base',
  lg: 'h-12 w-12 text-lg',
  xl: 'h-16 w-16 text-xl',
  '2xl': 'h-20 w-20 text-2xl',
};

interface AvatarContextValue {
  user:
    | { name?: string | null; userName?: string | null; username?: string | null; email?: string | null; id?: string | null; profile?: any }
    | string
    | null
    | undefined;
  size: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  initialsLength: 1 | 2;
  deterministicColor: boolean;
}

const AvatarContext = React.createContext<AvatarContextValue | undefined>(undefined);

const useAvatarContext = () => {
  const context = React.useContext(AvatarContext);
  if (!context) {
    throw new Error('Avatar compound components must be used within Avatar');
  }
  return context;
};

interface AvatarProps {
  user:
    | { name?: string | null; userName?: string | null; username?: string | null; email?: string | null; id?: string | null; profile?: any }
    | string
    | null
    | undefined;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  initialsLength?: 1 | 2;
  deterministicColor?: boolean;
  className?: string;
  children?: React.ReactNode;
}

const Avatar: React.FC<AvatarProps> = ({
  user,
  size = 'md',
  initialsLength = 2,
  deterministicColor = false,
  className,
  children,
}) => {
  return (
    <AvatarContext.Provider value={{ user, size, initialsLength, deterministicColor }}>
      <div className={cn('relative inline-flex items-center justify-center', className)}>
        {children || <AvatarFallback />}
      </div>
    </AvatarContext.Provider>
  );
};

interface AvatarFallbackProps {
  className?: string;
}

const AvatarFallback: React.FC<AvatarFallbackProps> = ({ className }) => {
  const { user, size, initialsLength, deterministicColor } = useAvatarContext();

  const initials = getAvatarText(user, initialsLength);

  let colorClass = 'bg-primary/10 text-primary';
  if (deterministicColor && user) {
    const colorSource = typeof user === 'string' ? user : (user.name || user.userName || user.username || user.email || user.id || '');
    const colorIndex = getAvatarColorIndex(colorSource);
    colorClass = avatarColors[colorIndex];
  }

  return (
    <div
      className={cn(
        'rounded-full flex items-center justify-center font-semibold',
        sizeClasses[size],
        colorClass,
        className
      )}
      aria-label={`Avatar with initials ${initials}`}
    >
      {initials}
    </div>
  );
};

interface AvatarImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  alt: string;
  fallbackDelayMs?: number;
}

const AvatarImage: React.FC<AvatarImageProps> = ({
  src,
  alt,
  fallbackDelayMs = 600,
  className,
  ...props
}) => {
  const { size } = useAvatarContext();
  const [hasError, setHasError] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);

  if (hasError || isLoading) {
    return (
      <>
        <img
          src={src}
          alt={alt}
          className={cn(
            'rounded-full object-cover transition-opacity duration-300',
            sizeClasses[size],
            isLoading ? 'opacity-0' : 'opacity-100',
            className
          )}
          onLoad={() => setIsLoading(false)}
          onError={() => setHasError(true)}
          {...props}
        />
        {(hasError || (isLoading && fallbackDelayMs === 0)) && <AvatarFallback />}
      </>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      className={cn('rounded-full object-cover', sizeClasses[size], className)}
      {...props}
    />
  );
};

export { Avatar, AvatarFallback, AvatarImage };
export default Avatar;
